import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Edit, RotateCcw, Trash2 } from 'lucide-react';
import { Type1EditDialog } from './Type1EditDialog';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';

interface Bot {
  id: string;
  name: string;
  type: string;
  status: string;
}

interface ConditionalSkipSetting {
  id: string;
  bot_id: string;
  enabled: boolean;
  forward_enabled: boolean;
  skip_type: string;
  loss_streak_threshold: number;
  max_loss_streak?: number | null;
  max_loss_behavior: string;
  forward_url: string | null;
  bots?: Bot;
}

interface BotState {
  id: string;
  bot_id: string;
  current_loss_streak: number;
  mode: 'GATE' | 'OPEN';
  opened_at: string | null;
  locked_by_max_loss: boolean;
}

interface Type1BotCardProps {
  setting: ConditionalSkipSetting;
  state?: BotState;
  onUpdate: (id: string, updates: any) => void;
  onReset: (botId: string) => void;
  onDelete: (id: string) => void;
  isUpdating: boolean;
  isResetting: boolean;
  isDeleting: boolean;
}

export const Type1BotCard: React.FC<Type1BotCardProps> = ({
  setting,
  state,
  onUpdate,
  onReset,
  onDelete,
  isUpdating,
  isResetting,
  isDeleting,
}) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const getModeColor = () => {
    if (state?.locked_by_max_loss) return 'destructive';
    if (state?.mode === 'OPEN') return 'default';
    return 'secondary';
  };

  const getModeText = () => {
    if (state?.locked_by_max_loss) return 'LOCKED';
    return state?.mode || 'GATE';
  };

  return (
    <>
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-3 sm:p-6 sm:pt-6">
          <div className="space-y-3 sm:space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm sm:text-base truncate">{setting.bots?.name || 'Unknown Bot'}</h4>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">{setting.bots?.type}</p>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Enabled</span>
                  <Switch
                    checked={setting.enabled}
                    onCheckedChange={(enabled) => onUpdate(setting.id, { enabled })}
                    disabled={isUpdating}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Forward</span>
                  <Switch
                    checked={setting.forward_enabled}
                    onCheckedChange={(forward_enabled) => onUpdate(setting.id, { forward_enabled })}
                    disabled={isUpdating || !setting.enabled}
                  />
                </div>
              </div>
            </div>

            {/* Tracking Only Badge */}
            {setting.enabled && !setting.forward_enabled && (
              <Badge variant="outline" className="text-xs">
                📋 Tracking Only (No Forward)
              </Badge>
            )}

            {/* Divider */}
            <div className="border-t" />

            {/* Current State */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
              <div>
                <span className="text-muted-foreground">Current:</span>
                <Badge variant={getModeColor()} className="ml-2 text-xs">
                  {getModeText()}
                </Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Streak:</span>
                <span className="ml-2 font-mono font-semibold">
                  {state?.current_loss_streak || 0}
                </span>
              </div>
            </div>

            {/* Configuration */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
              <div>
                <span className="text-muted-foreground">Threshold:</span>
                <span className="ml-2 font-semibold">{setting.loss_streak_threshold}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Max:</span>
                <span className="ml-2 font-semibold">
                  {setting.max_loss_streak || 'None'}
                </span>
              </div>
            </div>

            <div className="text-xs sm:text-sm">
              <span className="text-muted-foreground">Behavior:</span>
              <Badge variant="outline" className="ml-2 text-xs">
                {setting.max_loss_behavior === 'lock_gate' ? 'Lock Gate' : 'Reset Streak'}
              </Badge>
            </div>

            {/* Divider */}
            <div className="border-t" />

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onReset(setting.bot_id)}
                disabled={isResetting}
                className="flex-1 px-2 sm:px-3"
              >
                <RotateCcw className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">Reset</span>
              </Button>
              <Button
                size="sm"
                variant="default"
                onClick={() => setIsEditOpen(true)}
                className="flex-1 px-2 sm:px-3"
              >
                <Edit className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setIsDeleteOpen(true)}
                disabled={isDeleting}
                className="px-2 sm:px-3"
              >
                <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Type1EditDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        setting={setting}
        state={state}
        onUpdate={onUpdate}
        isUpdating={isUpdating}
      />

      <DeleteConfirmDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        botName={setting.bots?.name || 'Unknown Bot'}
        skipType="Type 1"
        onConfirm={() => {
          onDelete(setting.id);
          setIsDeleteOpen(false);
        }}
        isDeleting={isDeleting}
      />
    </>
  );
};
